import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import apiRequest, { ApiResponse } from "../axios/useApi";
import { calculateNetProfit } from "../pages/Accounting/Summaries";
import { UIContext } from "./UIProvider";
import { handleApiError } from "../lib/utils";
export interface AccountingContextType {
  isSummaryLoading: boolean;
  filteredTotalRevenue?: number;
  filteredTotalExpenses?: number;
  revenueData?: RevenueInterface[];
  expanseData?: RevenueInterface[];
  handleResetToCurrentMonth: () => void;
  handlePreviousMonth: () => void;
  handleNextMonth: () => void;
  currentDate: Date;
  isCurrentMonth: () => boolean;
  chartType: "line" | "bar";
  setChartType: (chartType: "line" | "bar") => void;
  netProfitData: NetProfitDataInterface[];
  isChartLoading: boolean;
}

export type ChartTypeInterface = "line" | "bar";

interface RevenueInterface {
  category: string;
  type: string;
  amount: number;
  value: string;
}

export const AccountingContext = createContext<AccountingContextType>({
  isSummaryLoading: true,
  filteredTotalRevenue: undefined,
  filteredTotalExpenses: undefined,
  revenueData: [],
  expanseData: [],
  handleResetToCurrentMonth: () => {},
  handlePreviousMonth: () => {},
  handleNextMonth: () => {},
  currentDate: new Date(),
  isCurrentMonth: () => false,
  chartType: "line",
  setChartType: () => {},
  netProfitData: [],
  isChartLoading: false,
});
type Props = PropsWithChildren;

export interface DataInterface {
  month: string;
  revenue: number;
  expenses: number;
}
interface NetProfitDataInterface {
  month: string;
  revenue: number;
  expense: number;
  profit: number;
}

interface ProfitLossItem {
  accountName: string;
  accountSubTypeName: string;
  amount: number;
  name: string;
  voucherDate: string;
}

export interface PieChartData {
  name: string;
  value: number;
  percentage: string;
  originalAmount: number;
}

export const AccountingProvider = ({ children }: Props) => {
  const { onError, onSuccess, businessId } = useContext(UIContext);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [revenueData, setRevenueData] = useState<RevenueInterface[] | []>();
  const [expanseData, setExpanseData] = useState<RevenueInterface[] | []>();
  const [chartType, setChartType] = useState<ChartTypeInterface>("line");

  const [currentDate, setCurrentDate] = useState(new Date());
  const [netProfitData, setNetProfitData] = useState<
    NetProfitDataInterface[] | []
  >([]);
  const isCurrentMonth = () => {
    const today = new Date();
    return (
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const handlePreviousMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() - 1);
    fetchData(newDate);
    fetchChartYearData(newDate);
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + 1);
    fetchData(newDate);
    fetchChartYearData(newDate);
    setCurrentDate(newDate);
  };

  const handleResetToCurrentMonth = () => {
    const today = new Date();
    fetchData(today);
    fetchChartYearData(today);
    setCurrentDate(today);
  };

  function groupRevenueExpenseByMonth(
    items: { amount: number; voucherDate: string; name: string }[]
  ) {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    // Group by month and sum revenue/expenses
    const grouped: { [month: string]: { revenue: number; expenses: number } } =
      {};

    items.forEach((item) => {
      const date = new Date(item.voucherDate);
      const monthKey = `${months[date.getMonth()]} ${date.getFullYear()}`;
      if (!grouped[monthKey]) grouped[monthKey] = { revenue: 0, expenses: 0 };
      if (item.name === "REVENUE") {
        grouped[monthKey].revenue += item.amount;
      }
      if (item.name === "EXPENSE") {
        grouped[monthKey].expenses += item.amount;
      }
    });

    // Convert to array for chart
    return Object.entries(grouped).map(([month, { revenue, expenses }]) => ({
      month,
      revenue: Number(revenue.toFixed(2)),
      expense: Number(expenses.toFixed(2)),
      profit: calculateNetProfit(revenue, expenses),
    }));
  }

  const groupAndCombineItems = (items: ProfitLossItem[]) => {
    const groupedItems = items.reduce(
      (acc: { [key: string]: RevenueInterface }, item) => {
        const key = item.accountName;
        if (!acc[key]) {
          acc[key] = {
            category: item.accountName,
            type: item.accountSubTypeName,
            amount: 0,
            value: "0%",
          };
        }
        acc[key].amount += item.amount;
        return acc;
      },
      {}
    );
    const allItems = Object.values(groupedItems);
    let positiveItems = allItems.filter((item) => item.amount > 0);
    const totalAmount = positiveItems.reduce(
      (sum, item) => sum + Math.abs(item.amount),
      0
    );

    const finalItems = allItems.map((item) => {
      if (item.amount <= 0) {
        return { ...item, value: "0%" }; // or "N/A" if you prefer
      }

      const percent = (item.amount / totalAmount) * 100;
      return {
        ...item,
        value: percent < 1 ? "<1%" : `${percent.toFixed(0)}%`,
      };
    });

    return finalItems;
  };

  // Format dates to YYYY-MM-DD
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchData = useCallback(async (date: Date) => {
    setIsSummaryLoading(true);
    try {
      // Get start and end dates of the current month
      const startDate = new Date(date.getFullYear(), date.getMonth(), 1);
      const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);

      const queryParams = new URLSearchParams();
      queryParams.append("FromDate", formatDate(startDate));
      queryParams.append("ToDate", formatDate(endDate));

      const response: ApiResponse<{
        profitLossItems: ProfitLossItem[];
        taxes: {
          amount: number;
          displayName: string;
          name: string;
        }[];
      }> = await apiRequest({
        url: `/Report/${businessId}/ProfitAndLossDashboard?${queryParams}`,
        method: "GET",
      });

      if (response.success && response.data?.profitLossItems) {
        onSuccess?.(
          "GET",
          "ProfitAndLossDashboard",
          response?.message || "Data retrieved successfully!"
        );
        const { profitLossItems } = response.data;

        // Filter and transform data based on name
        const revenueItems = groupAndCombineItems(
          profitLossItems.filter(
            (item: ProfitLossItem) => item.name === "REVENUE"
          )
        );
        const expenseItems = groupAndCombineItems(
          profitLossItems.filter(
            (item: ProfitLossItem) => item.name === "EXPENSE"
          )
        );

        setRevenueData(revenueItems);
        setExpanseData(expenseItems);
        // onSuccess?.(response?.message || "Data retrieved successfully!");
      } else {
        handleApiError(response, onError);
      }
    } catch (err) {
      onError?.(new Error("An error occurred while retirving data."));
    } finally {
      setIsSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentDate);
    fetchChartYearData(currentDate);
  }, []);

  const fetchChartYearData = async (date: Date) => {
    setIsChartLoading(true);
    const startDate = new Date(date.getFullYear(), 0, 1);
    const endDate = new Date(date.getFullYear(), 11, 31);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("FromDate", formatDate(startDate));
      queryParams.append("ToDate", formatDate(endDate));

      const response: ApiResponse<{
        profitLossItems: ProfitLossItem[];
      }> = await apiRequest({
        url: `/Report/${businessId}/ProfitAndLossDashboard?${queryParams}`,
        method: "GET",
      });
      if (response.success && response.data?.profitLossItems) {
        onSuccess?.(
          "GET",
          "ProfitAndLossDashboard",
          response?.message || "Data retrieved successfully!"
        );
        const { profitLossItems } = response.data;
        const chartData = groupRevenueExpenseByMonth(profitLossItems);
        setNetProfitData(chartData as any);
      }
    } catch (err) {
    } finally {
      setIsChartLoading(false);
    }
  };

  // Aggregate totals
  const filteredTotalRevenue = revenueData?.reduce(
    (sum, item) => sum + (item.amount ?? 0),
    0
  );

  const filteredTotalExpenses = expanseData?.reduce(
    (sum, item) => sum + (item.amount ?? 0),
    0
  );
  return (
    <AccountingContext.Provider
      value={{
        isSummaryLoading,
        filteredTotalRevenue,
        filteredTotalExpenses,
        revenueData,
        expanseData,
        handleResetToCurrentMonth,
        handlePreviousMonth,
        handleNextMonth,
        currentDate,
        isCurrentMonth,
        chartType,
        setChartType,
        netProfitData,
        isChartLoading,
      }}
    >
      {children}
    </AccountingContext.Provider>
  );
};
